import { useEffect, useState, type ReactNode } from "react";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import {
  RotateCcw,
  Loader2,
  ArrowLeft,
  Link2,
  Unlink,
  Search,
  Mail,
  ShoppingCart,
  Copy,
  Check,
  X,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import {
  useReturn,
  useUpdateReturnStatus,
  useUpdateReturnNote,
  useDeleteReturn,
  useLinkReturnOrder,
  useUnlinkReturnOrder,
  useResendReturnEmail,
} from "@/hooks/use-returns";
import { useStores } from "@/hooks/use-stores";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { useActiveStore } from "@/hooks/use-active-store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatDate, formatDateTime } from "@/lib/utils";
import { fetchJson } from "@/lib/api";
import { ReturnItemRow } from "@/components/returns/return-item-row";
import { ReturnCustomerForm } from "@/components/returns/return-customer-form";
import {
  STATUS_LABEL,
  STATUS_BADGE,
  REASON_LABEL,
  REQUEST_TYPE_LABEL,
  isExchangeRequest,
} from "@/lib/return-labels";

/** Boje statusa za traku toka. Svijetli način koristi tamnije nijanse (kontrast), tamni zadržava
 *  identitet aplikacije (-400). fill popunjava čvor/segment, text boji oznaku, border ocrtava
 *  sljedeći korak, ring označava trenutni čvor. */
const RAIL_COLOR: Record<string, { fill: string; text: string; border: string; ring: string }> = {
  new: {
    fill: "bg-blue-600",
    text: "text-blue-600 dark:text-blue-400",
    border: "border-blue-600 dark:border-blue-400",
    ring: "ring-blue-600 dark:ring-blue-400",
  },
  in_progress: {
    fill: "bg-yellow-500",
    text: "text-yellow-700 dark:text-yellow-400",
    border: "border-yellow-700 dark:border-yellow-400",
    ring: "ring-yellow-700 dark:ring-yellow-400",
  },
  approved: {
    fill: "bg-green-600",
    text: "text-green-700 dark:text-green-400",
    border: "border-green-700 dark:border-green-400",
    ring: "ring-green-700 dark:ring-green-400",
  },
  rejected: {
    fill: "bg-red-600",
    text: "text-red-600 dark:text-red-400",
    border: "border-red-600 dark:border-red-400",
    ring: "ring-red-600 dark:ring-red-400",
  },
  refund_completed: {
    fill: "bg-violet-600",
    text: "text-violet-700 dark:text-violet-400",
    border: "border-violet-700 dark:border-violet-400",
    ring: "ring-violet-700 dark:ring-violet-400",
  },
  exchange_completed: {
    fill: "bg-teal-600",
    text: "text-teal-700 dark:text-teal-400",
    border: "border-teal-700 dark:border-teal-400",
    ring: "ring-teal-700 dark:ring-teal-400",
  },
};

/** Boja za nepoznati status — neutralna plava, ista kao za "novo". */
const RAIL_COLOR_FALLBACK = {
  fill: "bg-blue-600",
  text: "text-blue-600 dark:text-blue-400",
  border: "border-blue-600 dark:border-blue-400",
  ring: "ring-blue-600 dark:ring-blue-400",
};

function railColor(status: string) {
  return RAIL_COLOR[status] ?? RAIL_COLOR_FALLBACK;
}

/** Sljedeći logički status u normalnom toku, ili null za terminalne statuse. */
function nextStatusFor(status: string, completionStatus: string): string | null {
  const NEXT: Record<string, string> = {
    new: "in_progress",
    in_progress: "approved",
    approved: completionStatus,
  };
  return NEXT[status] ?? null;
}

function copyToClipboard(text: string, message: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(message),
    () => toast.error("Kopiranje nije uspjelo"),
  );
}

/** Bosanska deklinacija za broj stavki. */
function stavkaCount(n: number): string {
  const last = n % 10;
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 14) return `${n} stavki`;
  if (last === 1) return `${n} stavka`;
  if (last >= 2 && last <= 4) return `${n} stavke`;
  return `${n} stavki`;
}

/** Tihi naslov kartice — mali uppercase eyebrow, u duhu terminalne estetike aplikacije. */
function CardHeader({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2 border-b bg-muted/50 px-4 py-2">
      <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">{title}</span>
      {right}
    </div>
  );
}

function Card({
  title,
  right,
  children,
  className,
}: {
  title: string;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`ring-1 ring-foreground/10 ${className ?? ""}`}>
      <CardHeader title={title} right={right} />
      {children}
    </section>
  );
}

/** Polje u mreži podataka: oznaka + sadržaj. */
function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ""}`}>
      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
      <div className="text-[13px] leading-relaxed break-words min-w-0">{children}</div>
    </div>
  );
}

type RailKind = "done" | "current" | "next" | "pending" | "muted" | "terminal";

export function ReturnDetailPage() {
  const { returnId } = useParams({ from: "/_app/returns/$returnId" });
  const navigate = useNavigate();
  const { data: item, isLoading, refetch } = useReturn(returnId);
  const { data: stores } = useStores();
  const updateStatus = useUpdateReturnStatus();
  const updateNote = useUpdateReturnNote();
  const deleteReturn = useDeleteReturn();
  const { user } = useSession();
  const canDelete = hasRole(user, "admin");
  const linkOrder = useLinkReturnOrder();
  const unlinkOrder = useUnlinkReturnOrder();
  const resendEmail = useResendReturnEmail();
  const { storeId: activeStoreId, setStoreId } = useActiveStore();
  const [note, setNote] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [orderLookup, setOrderLookup] = useState("");
  const [orderLookupLoading, setOrderLookupLoading] = useState(false);
  const [foundOrder, setFoundOrder] = useState<any | null>(null);

  // Preselektuj sljedeći logički status u toku kad god se trenutni promijeni.
  useEffect(() => {
    if (!item) return;
    const completion = isExchangeRequest(item.requestType) ? "exchange_completed" : "refund_completed";
    setSelectedStatus(nextStatusFor(item.status, completion) ?? "");
    setRejectReason("");
  }, [item?.status, item?.requestType]);

  if (isLoading) {
    return (
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-7" />
          <Skeleton className="h-6 w-40" />
        </div>
        <Skeleton className="h-10 w-full" />
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_300px]">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-8 flex flex-col items-center gap-2 text-center">
        <RotateCcw className="h-5 w-5 text-muted-foreground" />
        <p className="text-sm">Zahtjev nije pronađen</p>
        <Link to="/returns" className="text-xs text-blue-400 hover:underline">
          Nazad na listu zahtjeva
        </Link>
      </div>
    );
  }

  const store = stores?.find((s: any) => s.id === item.storeId);
  const noteValue = note ?? item.adminNote;
  const isExchange = isExchangeRequest(item.requestType);
  const completionStatus = isExchange ? "exchange_completed" : "refund_completed";
  const STATUS_FLOW = ["new", "in_progress", "approved", completionStatus];
  const nextStatus = nextStatusFor(item.status, completionStatus);
  // Terminalni statusi (odbijeno / izvršeno) nemaju sljedeći korak — kartica statusa se
  // svodi na email status i brisanje, bez mrtvih kontrola za promjenu.
  const isTerminal = !nextStatus;
  // "new" nema email šablon; bez email adrese kupca nema šta poslati.
  const willAskEmail = selectedStatus !== "" && selectedStatus !== "new" && !!item.email;

  // Traka toka: čvorovi su stvarni proces (sekvenca nosi informaciju), pa traka smije biti sekvenca.
  const currentIndex = item.status === "rejected" ? -1 : STATUS_FLOW.indexOf(item.status);
  const railSteps: { key: string; kind: RailKind }[] = STATUS_FLOW.map((s, i) => {
    let kind: RailKind;
    if (currentIndex === -1) kind = "muted";
    else if (i < currentIndex) kind = "done";
    else if (i === currentIndex) kind = "current";
    else if (i === currentIndex + 1) kind = "next";
    else kind = "pending";
    return { key: s, kind };
  });
  if (item.status === "rejected") railSteps.push({ key: "rejected", kind: "terminal" });

  async function handleStatusChange(sendEmail: boolean) {
    if (!selectedStatus) return;
    try {
      const updated = await updateStatus.mutateAsync({
        id: item!.id,
        status: selectedStatus,
        rejectionReason: selectedStatus === "rejected" ? rejectReason.trim() : undefined,
        sendEmail,
      });
      await refetch();
      toast.success(`Status ažuriran: ${STATUS_LABEL[selectedStatus]}`);
      if (sendEmail) {
        if (updated.emailStatus === "sent") {
          toast.success("Email poslan kupcu");
        } else {
          toast.error(updated.emailError || "Slanje email-a nije uspjelo");
        }
      }
    } catch (e: any) {
      toast.error(e.message || "Greška pri ažuriranju statusa");
    } finally {
      setConfirmOpen(false);
    }
  }

  async function handleLookupOrder() {
    const num = orderLookup.trim();
    if (!num) return;
    setOrderLookupLoading(true);
    setFoundOrder(null);
    try {
      const order = await fetchJson<any>(`/api/orders/${encodeURIComponent(num)}?storeId=${item!.storeId}`);
      setFoundOrder(order);
    } catch (e: any) {
      toast.error("Narudžba nije pronađena");
    } finally {
      setOrderLookupLoading(false);
    }
  }

  async function handleLinkOrder() {
    if (!foundOrder) return;
    try {
      await linkOrder.mutateAsync({
        id: item!.id,
        orderNumber: String(foundOrder.number || foundOrder.id),
        orderId: String(foundOrder.id),
      });
      await refetch();
      toast.success("Narudžba povezana");
      setOrderLookup("");
      setFoundOrder(null);
    } catch (e: any) {
      toast.error(e.message || "Greška pri povezivanju");
    }
  }

  async function handleUnlinkOrder() {
    try {
      await unlinkOrder.mutateAsync(item!.id);
      await refetch();
      toast.success("Veza s narudžbom uklonjena");
    } catch (e: any) {
      toast.error(e.message || "Greška");
    }
  }

  async function handleSaveNote() {
    try {
      await updateNote.mutateAsync({ id: item!.id, note: noteValue });
      toast.success("Bilješka sačuvana");
      setNote(null);
    } catch (e: any) {
      toast.error(e.message || "Greška pri spremanju bilješke");
    }
  }

  async function handleResendEmail() {
    try {
      const updated = await resendEmail.mutateAsync(item!.id);
      await refetch();
      if (updated.emailStatus === "sent") {
        toast.success("Email poslan");
      } else {
        toast.error(updated.emailError || "Slanje email-a nije uspjelo");
      }
    } catch (e: any) {
      toast.error(e.message || "Greška pri slanju email-a");
    }
  }

  async function handleDelete() {
    if (!confirm("Obrisati zahtjev za povrat?")) return;
    try {
      await deleteReturn.mutateAsync(item!.id);
      toast.success("Zahtjev obrisan");
      navigate({ to: "/returns" });
    } catch (e: any) {
      toast.error(e.message || "Greška pri brisanju");
    }
  }

  function handleCreateOrder() {
    // Forma nove narudžbe radi nad aktivnom trgovinom — mora biti ona iz zahtjeva,
    // inače bi narudžba otišla u pogrešan webshop.
    if (item!.storeId !== activeStoreId) setStoreId(item!.storeId);
    navigate({ to: "/orders/new", search: { returnId: item!.id } });
  }

  // Kartica promjene statusa — glavna radnja stranice. Renderuje se na vrhu (mobilni)
  // i prikovano u desnoj koloni (desktop) — isti JSX, samo druga pozicija.
  const statusCard = (className: string) => {
    // Terminalni status bez email statusa i bez prava brisanja nema šta prikazati.
    if (isTerminal && !item.emailStatus && !canDelete) return null;
    return (
    <Card
      title="Status"
      right={
        <Badge variant="outline" className={STATUS_BADGE[item.status]}>
          {STATUS_LABEL[item.status] || item.status}
        </Badge>
      }
      className={className}
    >
      <div className="p-3 flex flex-col gap-2">
        {!isTerminal && (
          <>
        <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Promijeni status</span>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Odaberi novi status..." />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FLOW.map((s) => (
              <SelectItem
                key={s}
                value={s}
                disabled={s === item.status}
                className={s === nextStatus ? "text-primary font-medium" : undefined}
              >
                {STATUS_LABEL[s]}
                {s === nextStatus && <span className="text-[10px] text-primary/80"> → sljedeći korak</span>}
              </SelectItem>
            ))}
            <SelectSeparator />
            <SelectItem value="rejected" disabled={item.status === "rejected"} className="text-red-400">
              {STATUS_LABEL.rejected}
            </SelectItem>
          </SelectContent>
        </Select>
        {selectedStatus === "rejected" && (
          <div className="flex flex-col gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Razlog odbijanja</span>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Npr. proizvod ima tragove nošenja..."
              className="min-h-[60px] text-xs"
              autoFocus
            />
          </div>
        )}
        <Button
          size="sm"
          className="w-full"
          onClick={() => setConfirmOpen(true)}
          disabled={
            updateStatus.isPending ||
            !selectedStatus ||
            selectedStatus === item.status ||
            (selectedStatus === "rejected" && !rejectReason.trim())
          }
        >
          {updateStatus.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
          Promijeni status
        </Button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Promjena statusa</AlertDialogTitle>
              <AlertDialogDescription>
                Promijeniti status u "{STATUS_LABEL[selectedStatus] || selectedStatus}"?
                {willAskEmail
                  ? ` Da li poslati email obavještenje kupcu (${item.email})?`
                  : selectedStatus !== "" && selectedStatus !== "new" && !item.email
                    ? " Kupac nema email adresu — obavještenje se ne šalje."
                    : ""}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Odustani</AlertDialogCancel>
              {willAskEmail ? (
                <>
                  <AlertDialogAction
                    className="bg-muted text-foreground hover:bg-muted/80"
                    onClick={() => handleStatusChange(false)}
                  >
                    Promijeni bez emaila
                  </AlertDialogAction>
                  <AlertDialogAction onClick={() => handleStatusChange(true)}>
                    Promijeni i pošalji email
                  </AlertDialogAction>
                </>
              ) : (
                <AlertDialogAction onClick={() => handleStatusChange(false)}>Promijeni</AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
          </>
        )}

        {item.emailStatus && (
          <div className="border-t pt-2 flex flex-col gap-1.5 text-[11px]">
            {item.emailStatus === "sent" ? (
              <span className="flex items-center gap-1.5 text-green-500">
                <Mail className="h-3 w-3 shrink-0" />
                <span>
                  Email poslan
                  {item.emailSentAt ? ` · ${formatDate(new Date(item.emailSentAt * 1000).toISOString())}` : ""}
                </span>
              </span>
            ) : (
              <>
                <span className="flex items-center gap-1.5 text-red-400">
                  <Mail className="h-3 w-3 shrink-0" />
                  <span>Email nije poslan</span>
                  {item.emailStatus === "failed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 ml-auto text-[11px]"
                      onClick={handleResendEmail}
                      disabled={resendEmail.isPending}
                    >
                      {resendEmail.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "Pošalji ponovo"}
                    </Button>
                  )}
                </span>
                {item.emailError && (
                  <span className="text-muted-foreground whitespace-pre-wrap">{item.emailError}</span>
                )}
              </>
            )}
          </div>
        )}

        {canDelete && (
          <div className="border-t pt-2 mt-0.5">
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteReturn.isPending}
              className="text-[11px] text-muted-foreground hover:text-red-400 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Obriši zahtjev
            </button>
          </div>
        )}
      </div>
    </Card>
    );
  };

  return (
    <div className="p-4 flex flex-col gap-3">
      {/* Zaglavlje — referenca je teza stranice: operateri komuniciraju preko nje. */}
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/returns">
            <Button variant="ghost" size="icon-xs" aria-label="Nazad na listu zahtjeva">
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <div className="flex items-center gap-1.5 min-w-0">
            <h1 className="text-2xl font-mono font-semibold tracking-tight truncate">{item.referenceNumber}</h1>
            <button
              type="button"
              onClick={() => copyToClipboard(item.referenceNumber, "Referenca kopirana")}
              title="Kopiraj referencu"
              aria-label="Kopiraj referencu"
              className="text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="text-muted-foreground">
              {REQUEST_TYPE_LABEL[item.requestType] || item.requestType}
            </Badge>
            <Badge variant="outline" className={STATUS_BADGE[item.status]}>
              {STATUS_LABEL[item.status] || item.status}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">
            {item.firstName} {item.lastName}
          </span>
          {store && (
            <>
              <span className="text-muted-foreground/40" aria-hidden="true">·</span>
              <span>{store.name}</span>
            </>
          )}
          <span className="text-muted-foreground/40" aria-hidden="true">·</span>
          <span>Kreirano {formatDate(new Date(item.createdAt * 1000).toISOString())}</span>
          {item.phone && (
            <>
              <span className="text-muted-foreground/40" aria-hidden="true">·</span>
              <span>{item.phone}</span>
            </>
          )}
        </div>
      </header>

      {/* Potpis stranice: traka toka statusa. Svi čvorovi iste veličine — os i poravnanje
          segmenta nose tačnost, a trenutni korak se označava prstenom, ne veličinom. */}
      <div className="flex items-start pt-1" role="group" aria-label="Tok statusa">
        {railSteps.map((step, i) => {
          const color = railColor(step.key);
          const isLast = i === railSteps.length - 1;
          const label = STATUS_LABEL[step.key] || step.key;
          let nodeClass = "h-3 w-3 border border-foreground/25 bg-transparent";
          let labelClass = "text-muted-foreground";
          if (step.kind === "done") {
            nodeClass = `h-3 w-3 ${color.fill}`;
            labelClass = color.text;
          } else if (step.kind === "current") {
            nodeClass = `h-3 w-3 ${color.fill} ring-2 ring-offset-1 ring-offset-background ${color.ring}`;
            labelClass = `${color.text} font-semibold`;
          } else if (step.kind === "terminal") {
            nodeClass = `h-3 w-3 ${color.fill} ring-2 ring-offset-1 ring-offset-background ${color.ring}`;
            labelClass = `${color.text} font-semibold`;
          } else if (step.kind === "next") {
            nodeClass = `h-3 w-3 border-2 ${color.border} bg-transparent motion-safe:animate-pulse`;
            labelClass = color.text;
          } else if (step.kind === "muted") {
            nodeClass = "h-3 w-3 border border-foreground/15 bg-transparent";
            labelClass = "text-muted-foreground";
          }
          const trackClass = step.kind === "done" ? color.fill : "bg-foreground/20";
          return (
            <div
              key={step.key}
              className={`${isLast ? "shrink-0" : "flex-1 min-w-0"} motion-safe:animate-[rail-in_0.35s_ease-out_both]`}
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <div className="flex items-center">
                <span className={`shrink-0 ${nodeClass}`} aria-hidden="true" />
                {!isLast && <span className={`mx-2 h-[2px] flex-1 ${trackClass}`} aria-hidden="true" />}
              </div>
              <div
                className={`mt-1.5 text-[11px] font-medium truncate ${labelClass}`}
                title={step.kind === "next" ? `${label} — sljedeći korak` : label}
              >
                {label}
              </div>
            </div>
          );
        })}
      </div>

      {item.status === "rejected" && item.rejectionReason && (
        <div className="flex gap-2.5 border border-red-500/30 bg-red-500/5 px-4 py-3">
          <X className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10px] font-medium uppercase tracking-[0.14em] text-red-400">Razlog odbijanja</div>
            <div className="mt-1 text-xs whitespace-pre-wrap">{item.rejectionReason}</div>
          </div>
        </div>
      )}

      {/* Mobilni: glavna radnja odmah uz traku toka — ne zakopava se ispod podataka o kupcu. */}
      <div className="md:hidden">{statusCard("")}</div>

      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_300px] items-start">
        {/* Lijeva kolona: radna površina — artikli pa podaci o kupcu. */}
        <div className="flex flex-col gap-3 min-w-0">
          <Card title="Artikli" right={<span className="text-[10px] text-muted-foreground">{stavkaCount(item.items.length)}</span>}>
            <div>
              {item.items.map((it, idx) => {
                const returnedLink = item.links?.find((l) => l.role === "returned" && l.itemIndex === idx);
                const exchangeLink = item.links?.find((l) => l.role === "exchange" && l.itemIndex === idx);
                const hasExchangeText = !!(it.exchangeModel || it.exchangeColor || it.exchangeSize);
                return (
                  <div key={idx} className="border-b last:border-b-0 px-4 py-3 flex gap-3">
                    <span className="w-5 shrink-0 pt-px text-right font-mono text-[11px] text-muted-foreground/60">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                      <ReturnItemRow
                        returnId={item.id}
                        storeId={item.storeId}
                        role="returned"
                        itemIndex={idx}
                        parts={[it.model, it.color, it.size]}
                        link={returnedLink}
                      />
                      {isExchange && hasExchangeText && (
                        <div className="flex items-center gap-2 pl-0.5">
                          <span className="text-muted-foreground/50 text-xs shrink-0" aria-hidden="true">→</span>
                          <div className="flex-1 min-w-0">
                            <ReturnItemRow
                              returnId={item.id}
                              storeId={item.storeId}
                              role="exchange"
                              itemIndex={idx}
                              parts={[it.exchangeModel, it.exchangeColor, it.exchangeSize]}
                              link={exchangeLink}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {item.additionalItems?.length > 0 && (
            <Card
              title="Dodatno naručeni proizvodi"
              right={<span className="text-[10px] text-muted-foreground">{stavkaCount(item.additionalItems.length)}</span>}
            >
              <div>
                {item.additionalItems.map((it, idx) => {
                  const additionalLink = item.links?.find((l) => l.role === "additional" && l.itemIndex === idx);
                  return (
                    <div key={idx} className="border-b last:border-b-0 px-4 py-3 flex gap-3">
                      <span className="w-5 shrink-0 pt-px text-right font-mono text-[11px] text-muted-foreground/60">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div className="flex-1 min-w-0">
                        <ReturnItemRow
                          returnId={item.id}
                          storeId={item.storeId}
                          role="additional"
                          itemIndex={idx}
                          parts={[it.model, it.color, it.size]}
                          link={additionalLink}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          <Card
            title="Kupac"
            right={
              !editingCustomer && (
                <button
                  type="button"
                  onClick={() => setEditingCustomer(true)}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Pencil className="h-3 w-3" />
                  Uredi
                </button>
              )
            }
          >
            {editingCustomer ? (
              <ReturnCustomerForm item={item} onDone={() => setEditingCustomer(false)} />
            ) : (
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Ime i prezime">
                {item.firstName} {item.lastName}
              </Field>
              <Field label="Trgovina">{store?.name || "—"}</Field>
              <Field label="Email">{item.email || "—"}</Field>
              <Field label="Telefon">{item.phone || "—"}</Field>
              <Field label="Broj narudžbe">
                {item.linkedOrderId ? (
                  <div className="flex items-center gap-1.5">
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: item.linkedOrderId }}
                      className="font-mono text-blue-400 hover:underline"
                    >
                      #{item.orderNumber}
                    </Link>
                    <button
                      type="button"
                      onClick={handleUnlinkOrder}
                      disabled={unlinkOrder.isPending}
                      title="Ukloni vezu s narudžbom"
                      className="text-muted-foreground hover:text-red-400 disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Unlink className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <span className="text-muted-foreground">{item.orderNumber || "—"}</span>
                )}
              </Field>
              <Field label="Tip zahtjeva">{REQUEST_TYPE_LABEL[item.requestType] || item.requestType}</Field>
              {isExchange && (
                <Field label="Adresa dostave (zamjena)" className="col-span-full">
                  {item.deliveryAddress ? (
                    <div className="flex items-center gap-1.5">
                      <span>{`${item.deliveryAddress}, ${item.deliveryPostalCode} ${item.deliveryCity}`}</span>
                      <button
                        type="button"
                        onClick={() =>
                          copyToClipboard(
                            `${item.deliveryAddress}, ${item.deliveryPostalCode} ${item.deliveryCity}`,
                            "Adresa kopirana",
                          )
                        }
                        title="Kopiraj adresu"
                        aria-label="Kopiraj adresu"
                        className="text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    "—"
                  )}
                </Field>
              )}
              {item.requestType === "vraca" && (
                <Field label="Žiro račun" className="col-span-full">
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono">{item.bankAccount || "—"}</span>
                    {item.bankAccount && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(item.bankAccount, "Žiro račun kopiran")}
                        title="Kopiraj žiro račun"
                        aria-label="Kopiraj žiro račun"
                        className="text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </Field>
              )}
              <Field label="Razlozi" className="col-span-full">
                {item.reasons.length === 0 ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {item.reasons.map((r) => (
                      <span key={r} className="text-xs px-2 py-0.5 border bg-muted/30">
                        {REASON_LABEL[r] || r}
                      </span>
                    ))}
                  </div>
                )}
              </Field>
              <Field label="Izjava" className="col-span-full">
                {item.declaration ? (
                  <span className="flex items-center gap-1.5 text-green-500">
                    <Check className="h-3.5 w-3.5 shrink-0" />
                    Potvrđeno: proizvodi nemaju tragove nošenja i nisu oštećeni.
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <X className="h-3.5 w-3.5 shrink-0" />
                    Nije potvrđeno
                  </span>
                )}
              </Field>
            </div>
            )}
          </Card>
        </div>

        {/* Desna kolona: akcije. Status je prikovan — glavna radnja je uvijek pri ruci. */}
        <div className="flex flex-col gap-3">
          {statusCard("hidden md:block md:sticky md:top-3 z-10 bg-background")}

          {!item.linkedOrderId && (
            <Card title="Narudžba">
              <div className="p-3 flex flex-col gap-2">
                {isExchange && (
                  <>
                    <Button size="sm" className="w-full" onClick={handleCreateOrder}>
                      <ShoppingCart className="h-3 w-3 mr-1.5" /> Kreiraj narudžbu
                    </Button>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70">
                      <span className="h-px flex-1 bg-border" aria-hidden="true" />
                      ili poveži postojeću
                      <span className="h-px flex-1 bg-border" aria-hidden="true" />
                    </div>
                  </>
                )}
                <div className="flex gap-1.5">
                  <Input
                    value={orderLookup}
                    onChange={(e) => {
                      setOrderLookup(e.target.value);
                      setFoundOrder(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleLookupOrder();
                      }
                    }}
                    placeholder={item.orderNumber ? `npr. ${item.orderNumber}` : "Broj narudžbe"}
                    className="text-xs h-8"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleLookupOrder}
                    disabled={orderLookupLoading || !orderLookup.trim()}
                    aria-label="Traži narudžbu"
                  >
                    {orderLookupLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                  </Button>
                </div>
                {foundOrder && (
                  <div className="flex flex-col gap-2 border-t pt-2">
                    <div className="text-xs">
                      <div className="font-medium font-mono">#{foundOrder.number}</div>
                      <div className="text-muted-foreground">
                        {foundOrder.billing?.first_name} {foundOrder.billing?.last_name}
                      </div>
                      <div className="text-muted-foreground">
                        {foundOrder.total} {foundOrder.currency} · {foundOrder.status}
                      </div>
                    </div>
                    <Button size="sm" className="w-full" onClick={handleLinkOrder} disabled={linkOrder.isPending}>
                      {linkOrder.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
                      <Link2 className="h-3 w-3 mr-1.5" /> Poveži s ovom narudžbom
                    </Button>
                  </div>
                )}
                {isExchange && (
                  <span className="text-[11px] text-muted-foreground">
                    Nova narudžba se otvara s kupcem i zamjenskim artiklima već popunjenim.
                  </span>
                )}
              </div>
            </Card>
          )}

          <Card title="Interna bilješka">
            <div className="p-3 flex flex-col gap-2">
              <Textarea
                value={noteValue}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Dodaj bilješku..."
                className="min-h-[80px] text-xs"
              />
              <Button size="sm" variant="outline" className="w-full" onClick={handleSaveNote} disabled={updateNote.isPending}>
                {updateNote.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1.5" />}
                Sačuvaj bilješku
              </Button>
            </div>
          </Card>
        </div>
      </div>

      <footer className="flex flex-wrap items-center justify-between gap-1 text-[11px] text-muted-foreground">
        <span>Kreirano {formatDateTime(new Date(item.createdAt * 1000).toISOString())}</span>
        <span>Ažurirano {formatDateTime(new Date(item.updatedAt * 1000).toISOString())}</span>
      </footer>
    </div>
  );
}
